import { computed, inject, Injectable, signal } from '@angular/core';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import {
  defaultInteractionLogFilters,
  InteractionLogFilters,
  InteractionLogRecord,
  InteractionQueryType,
} from '../models/interaction-log.model';
import { InteractionLogRepository } from '../services/interaction-log/interaction-log.repository';

export type InteractionLogStatus = 'loading' | 'ready' | 'empty' | 'error';

const emptyPage = (size: number): PageMeta => ({
  page: 0,
  size,
  totalElements: 0,
  totalPages: 1,
});

const ERROR_SUBJECT = 'el historial de interacciones';

@Injectable()
export class InteractionLogStore {
  private readonly repository = inject(InteractionLogRepository);

  private readonly _status = signal<InteractionLogStatus>('loading');
  private readonly _records = signal<readonly InteractionLogRecord[]>([]);
  private readonly _pageMeta = signal<PageMeta>(emptyPage(DEFAULT_PAGE_SIZE));
  private readonly _filters = signal<InteractionLogFilters>(defaultInteractionLogFilters());
  private readonly _availableQueryTypes = signal<readonly InteractionQueryType[]>([]);
  private readonly _error = signal<AppError | null>(null);

  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly availableQueryTypes = this._availableQueryTypes.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly resultCountLabel = computed(() => `${this._pageMeta().totalElements} interacción(es)`);

  search(filters: InteractionLogFilters): void {
    this._filters.set(filters);
    this.load(0);
  }

  load(page = 0, size = this._pageMeta().size): void {
    this._error.set(null);
    this._status.set('loading');

    this.repository
      .getInteractionLog({ page, size, sortBy: 'fecha', sortOrder: 'desc', ...this._filters() })
      .subscribe({
        next: (result) => {
          this._records.set(result.content);
          this._pageMeta.set(result.page);
          this._availableQueryTypes.set(result.availableQueryTypes);
          this._status.set(result.page.totalElements === 0 ? 'empty' : 'ready');
        },
        error: (cause: unknown) => {
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._records.set([]);
          this._pageMeta.set(emptyPage(size));
          this._status.set('error');
        },
      });
  }
}
